import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import Spinner from '../../components/Spinner'
import { toast } from '../../components/Toast'

type Celebrity = {
  name: string
  category: string
  similarity: number
  day_gan: string
  element_wood: number; element_fire: number; element_earth: number
  element_metal: number; element_water: number
  description?: string
  match_reason?: string
}

const CATEGORY_FILTERS = ['전체', '배우', '가수', '운동선수', '기업인']

const MAX_BLOCKS = 8

function ElementBlocks({ value, color }: { value: number; color: string }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: MAX_BLOCKS }).map((_, i) => (
        <div
          key={i}
          className={`w-3 h-3 rounded-sm border ${i < value ? `${color} border-transparent` : 'bg-[var(--color-border-default)] border-[var(--color-border-default)]'}`}
        />
      ))}
    </div>
  )
}

function ElementMini({ wood, fire, earth, metal, water }: { wood: number; fire: number; earth: number; metal: number; water: number }) {
  return (
    <div className="space-y-1 mt-2">
      {[
        { v: wood, color: 'bg-green-500', label: '목' },
        { v: fire, color: 'bg-red-500', label: '화' },
        { v: earth, color: 'bg-yellow-600', label: '토' },
        { v: metal, color: 'bg-gray-400', label: '금' },
        { v: water, color: 'bg-blue-500', label: '수' },
      ].map((e) => (
        <div key={e.label} className="flex items-center gap-2">
          <span className="text-[var(--color-text-tertiary)] text-[10px] w-3 shrink-0">{e.label}</span>
          <ElementBlocks value={e.v} color={e.color} />
          <span className="text-[var(--color-text-disabled)] text-[10px]">{e.v}</span>
        </div>
      ))}
    </div>
  )
}

export default function CelebrityMatch() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [celebrities, setCelebrities] = useState<Celebrity[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)
  const [filter, setFilter] = useState('전체')
  const [mySaju, setMySaju] = useState<any>(null)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    client.get('/users/me/saju').then((r) => setMySaju(r.data)).catch(() => {})
  }, [isAuthenticated])

  const handleFind = async () => {
    setLoading(true)
    try {
      const { data } = await client.get('/features/celebrity-match')
      setCelebrities(data)
      setFetched(true)
    } catch {
      toast('유명인 매칭에 실패했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const filtered = filter === '전체' ? celebrities : celebrities.filter((c) => c.category === filter)
  const medals = ['🏆', '🥈', '🥉']

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">👤 유명인 사주 비교</h1>
        </div>

        {/* 내 오행 */}
        {mySaju && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-4 mb-4">
            <p className="text-[var(--color-text-tertiary)] text-xs mb-2">나의 오행 분포</p>
            <div className="space-y-1.5">
              {[
                { label: '목', value: mySaju.element_wood, color: 'bg-green-500' },
                { label: '화', value: mySaju.element_fire, color: 'bg-red-500' },
                { label: '토', value: mySaju.element_earth, color: 'bg-yellow-600' },
                { label: '금', value: mySaju.element_metal, color: 'bg-gray-400' },
                { label: '수', value: mySaju.element_water, color: 'bg-blue-500' },
              ].map((e) => (
                <div key={e.label} className="flex items-center gap-2">
                  <span className="text-[var(--color-text-tertiary)] text-xs w-4 shrink-0">{e.label}</span>
                  <ElementBlocks value={e.value} color={e.color} />
                  <span className="text-[var(--color-text-disabled)] text-xs w-3">{e.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={handleFind} disabled={loading}
          className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-60 text-[var(--color-primary)] rounded-xl font-bold transition-opacity flex items-center justify-center gap-2 mb-4">
          {loading ? <><Spinner size="sm" /> 럭키가 찾는 중</> : '🔮 나와 닮은 유명인 찾기'}
        </button>

        {fetched && celebrities.length > 0 && (
          <>
            {/* 카테고리 필터 */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
              {CATEGORY_FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? 'bg-[var(--color-accent)] text-[var(--color-primary)]' : 'bg-[var(--color-surface)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]'}`}>
                  {f}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {filtered.map((c, i) => (
                <div key={c.name} className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{medals[i] || '⭐'}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[var(--color-text-primary)] font-semibold">{c.name}</p>
                        <span className="text-[var(--color-accent)] text-sm font-bold">유사도 {Math.round(c.similarity * 100)}%</span>
                      </div>
                      <p className="text-[var(--color-text-tertiary)] text-xs">{c.category} · {c.day_gan} 일간</p>
                      {c.match_reason && (
                        <p className="text-[var(--color-accent)] text-xs mt-1 font-medium">🔍 {c.match_reason}</p>
                      )}
                      {c.description && <p className="text-[var(--color-text-tertiary)] text-xs mt-1">{c.description}</p>}
                      <ElementMini wood={c.element_wood} fire={c.element_fire} earth={c.element_earth} metal={c.element_metal} water={c.element_water} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {fetched && filtered.length === 0 && (
          <div className="text-center text-[var(--color-text-tertiary)] py-10">
            <p>해당 카테고리에 유명인이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  )
}
